import { useEffect, useState } from 'react';
import { branchesApi } from '../../lib/api';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Field, Select, TextInput } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Can } from '../../lib/permissions';
import { useShortcuts } from '../../lib/keys';
import type { Branch } from '../../lib/types';

const STATES = ['27-Maharashtra', '29-Karnataka', '33-Tamil Nadu', '07-Delhi', '24-Gujarat'];

export function BranchesPage() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [gstin, setGstin] = useState('');
  const [stateCode, setStateCode] = useState(STATES[0]!);
  const [registrationType, setRegistrationType] = useState<'regular' | 'composition' | 'unregistered' | 'sez'>('regular');

  function load() {
    branchesApi.list().then(({ items }) => setBranches(items)).catch(setError);
  }

  useEffect(load, []);

  useShortcuts('Branches', [
    { id: 'new-branch', bindings: [{ key: 'n', alt: true }], description: 'New branch', handler: () => setModalOpen(true) },
  ]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await branchesApi.create({ name, gstin: gstin || null, stateCode, registrationType });
      setModalOpen(false);
      setName('');
      setGstin('');
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const columns: DataTableColumn<Branch>[] = [
    { key: 'name', header: 'Branch', render: (b) => <span>{b.name}{b.isPrimary && <span className="ml-1.5 kbd">primary</span>}</span> },
    { key: 'gstin', header: 'GSTIN', render: (b) => <span className="mono">{b.gstin ?? '—'}</span> },
    { key: 'state', header: 'State', render: (b) => b.stateCode },
    { key: 'reg', header: 'Registration', render: (b) => b.registrationType },
  ];

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Branches</h1>
        <Can permission="company:branch:create">
          <button onClick={() => setModalOpen(true)} className="rounded-[3px] border border-[var(--border)] px-2 py-1 text-[12px] hover:bg-[var(--bg-hover)]">
            New branch <span className="kbd ml-1">Alt+N</span>
          </button>
        </Can>
      </div>
      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}
      <DataTable columns={columns} rows={branches} getRowKey={(b) => b.id} emptyMessage="No branches yet." />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New branch">
        <form onSubmit={handleCreate} className="flex flex-col gap-3">
          <Field label="Name" htmlFor="b-name" required>
            <TextInput id="b-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="GSTIN" htmlFor="b-gstin">
            <TextInput id="b-gstin" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} maxLength={15} />
          </Field>
          <Field label="State" htmlFor="b-state" required>
            <Select id="b-state" value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Registration type" htmlFor="b-reg" required>
            <Select id="b-reg" value={registrationType} onChange={(e) => setRegistrationType(e.target.value as typeof registrationType)}>
              <option value="regular">Regular</option>
              <option value="composition">Composition</option>
              <option value="unregistered">Unregistered</option>
              <option value="sez">SEZ</option>
            </Select>
          </Field>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50">
              {busy ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
